'use client';

import React, { useState, useEffect } from 'react';
import Breadcrumb from '../../components/Breadcrumb';
import { useParams, useNavigate } from 'react-router-dom';
import api from '../../services/api';
import {
  Edit2, ArrowLeft, Clock, FileText, AlertCircle, ChevronRight, Info, Share2, CheckCircle, Timer,
  Flag, Repeat, UserCog, History
} from 'lucide-react';
import alertService from '../../utils/alert';
import { cn } from '../../utils/cn';
import ShareModal from '../../components/crm/ShareModal';
import TransferOwnershipModal from '../../components/crm/TransferOwnershipModal';
import RelatedFiles from '../../components/crm/RelatedFiles';
import AuditHistory from '../../components/crm/AuditHistory';
import { formatDate, formatDateTime } from '../../utils/formatDate';
import { PageLoader } from '../../components/crm/ui';

const TABS = [
  { id: 'overview', label: 'Details', icon: Info },
  { id: 'files', label: 'Files', icon: FileText },
  { id: 'history', label: 'History', icon: History },
];

const TASK_TYPES = ['call', 'email', 'whatsapp', 'meeting', 'follow_up', 'custom', 'note', 'other', 'technical'];
const PRIORITIES = ['low', 'medium', 'high', 'urgent'];

export default function TaskDetailPage() {
  const { taskId } = useParams();
  const navigate = useNavigate();

  const [task, setTask] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saveLoading, setSaveLoading] = useState(false);
  const [error, setError] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [formData, setFormData] = useState(null);
  const [activeTab, setActiveTab] = useState('overview');
  const [showShareModal, setShowShareModal] = useState(false);
  const [showTransferModal, setShowTransferModal] = useState(false);

  useEffect(() => { fetchTaskData(); }, [taskId]);

  const fetchTaskData = async () => {
    try {
      setLoading(true);
      const response = await api.get(`/crm/tasks/${taskId}`);
      if (response.data.success) {
        setTask(response.data.task);
        setFormData(response.data.task);
      } else setError('Failed to load record.');
    } catch (err) {
      setError(err.response?.data?.message || 'Access denied.');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    try {
      setSaveLoading(true);
      await api.put(`/crm/tasks/${taskId}`, formData);
      setTask(formData);
      setIsEditing(false);
      alertService.success('Success', 'Task directive synchronized');
    } catch (err) {
      alertService.error('Error', 'Failed to update record');
    } finally {
      setSaveLoading(false);
    }
  };

  const handleComplete = async () => {
    try {
      setSaveLoading(true);
      const res = await api.post(`/crm/tasks/${taskId}/complete`);
      if (res.data.success) {
        setTask(res.data.task);
        setFormData(res.data.task);
        alertService.success('Resolved', 'Task marked as completed');
      }
    } catch (err) {
      alertService.error('Error', 'Failed to complete task');
    } finally {
      setSaveLoading(false);
    }
  };

  if (loading) {
    return (
      <PageLoader />
    );
  }

  if (!task) {
    return (
      <div className="p-12 text-center space-y-4">
        <div className="w-16 h-16 bg-[#F4F6F9] rounded-full flex items-center justify-center mx-auto text-[#706E6B]">
          <AlertCircle size={32} />
        </div>
        <h2 className="text-xl font-bold text-[#2E2E2E]">Record Not Found</h2>
        <button onClick={() => navigate('/dashboard/crm/tasks')} className="text-[#0176D3] font-bold text-sm hover:underline flex items-center justify-center gap-2 mx-auto">
          <ArrowLeft size={16} /> Return to Tasks
        </button>
      </div>
    );
  }

  const isClosed = task.status === 'completed' || task.status === 'cancelled';

  return (
    <div className="p-3 sm:p-6 lg:p-8 space-y-6 sm:max-w-[100rem] mx-auto animate-in fade-in slide-in-from-bottom-2 duration-500">

      {/* Dynamic Path Navigation */}
      <Breadcrumb className="mb-2" items={[{ label: 'Tasks', to: '/dashboard/crm/tasks' }, { label: task.title }]} />

      {/* Profile Header */}
      <div className="bg-white border border-[#E5E5E5] rounded-lg shadow-[0_1px_3px_rgba(0,0,0,0.08)] p-4">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
          <div className="flex items-center gap-5">
            <div className="w-10 h-10 shrink-0 rounded-lg bg-[#0176D3] flex items-center justify-center text-white">
              <Clock size={22} />
            </div>
            <div className="space-y-1">
              <div className="flex items-center gap-3">
                <h1 className="text-lg sm:text-xl font-bold text-[#2E2E2E]">{isEditing ? 'Edit Task' : task.title}</h1>
                <span className={cn('sf-badge', isClosed ? 'sf-badge-success' : 'sf-badge-primary')}>{(task.status || 'pending').toUpperCase()}</span>
              </div>
              <div className="flex items-center gap-4 text-[12px] text-[#706E6B] font-medium">
                <span className="flex items-center gap-1.5"><Flag size={12} /> {(task.priority || 'medium').toUpperCase()}</span>
                <span className="flex items-center gap-1.5"><Repeat size={12} /> {(task.type || 'follow_up').toUpperCase()}</span>
              </div>
            </div>
          </div>

          <div className="flex items-center gap-2">
            {isEditing ? (
              <>
                <button onClick={() => setIsEditing(false)} className="h-9 px-4 border border-[#C9C9C9] rounded whitespace-nowrap shrink-0 text-[13px] font-semibold hover:bg-[#F3F2F2]">Cancel</button>
                <button onClick={handleSave} disabled={saveLoading} className="h-9 px-4 bg-[#0176D3] text-white rounded whitespace-nowrap shrink-0 text-[13px] font-semibold hover:bg-[#014486]">Save</button>
              </>
            ) : (
              <>
                <button onClick={() => setIsEditing(true)} className="h-9 px-4 bg-white border border-[#C9C9C9] rounded whitespace-nowrap shrink-0 text-[13px] font-semibold hover:bg-[#F3F2F2] flex items-center gap-2">
                  <Edit2 size={14} /> Edit
                </button>
                {!isClosed && (
                  <button onClick={handleComplete} disabled={saveLoading} className="h-9 px-4 bg-[#2E844A] text-white rounded whitespace-nowrap shrink-0 text-[13px] font-semibold hover:bg-[#1E6334] flex items-center gap-2">
                    <CheckCircle size={14} /> Resolve
                  </button>
                )}
                <button onClick={() => setShowShareModal(true)} className="h-9 px-4 bg-white border border-[#C9C9C9] rounded whitespace-nowrap shrink-0 text-[13px] font-semibold hover:bg-[#F3F2F2] flex items-center gap-2">
                  <Share2 size={14} /> Share
                </button>
                <button onClick={() => setShowTransferModal(true)} className="h-9 px-4 bg-white border border-[#C9C9C9] rounded whitespace-nowrap shrink-0 text-[13px] font-semibold hover:bg-[#F3F2F2] flex items-center gap-2">
                  <UserCog size={14} /> Transfer
                </button>
              </>
            )}
          </div>
        </div>

        {/* Highlight Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-8 pt-6 border-t border-[#D8DDE6]">
          <div className="space-y-1">
            <p className="text-[10px] font-bold text-[#706E6B] uppercase tracking-wider">Due Date</p>
            <p className="text-[13px] font-bold text-[#2E2E2E]">{task.dueDate ? formatDateTime(task.dueDate) : 'Not set'}</p>
          </div>
          <div className="space-y-1">
            <p className="text-[10px] font-bold text-[#706E6B] uppercase tracking-wider">Duration</p>
            <p className="text-[13px] font-bold text-[#2E2E2E]">{task.duration ? `${task.duration} min` : '—'}</p>
          </div>
          <div className="space-y-1">
            <p className="text-[10px] font-bold text-[#706E6B] uppercase tracking-wider">Assigned To</p>
            <p className="text-[13px] font-bold text-[#2E2E2E]">{task.assignedTo || 'Unassigned'}</p>
          </div>
          <div className="space-y-1">
            <p className="text-[10px] font-bold text-[#706E6B] uppercase tracking-wider">Completed On</p>
            <p className="text-[13px] font-bold text-[#2E2E2E]">{task.completedAt ? formatDate(task.completedAt) : '—'}</p>
          </div>
        </div>

        {/* Ownership & Audit */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-4 pt-4 border-t border-[#D8DDE6]">
          <div className="space-y-1">
            <p className="text-[10px] font-bold text-[#706E6B] uppercase tracking-wider">Record Owner</p>
            <p className="text-[13px] font-medium text-[#2E2E2E]">{task.ownerName || 'Unassigned'}</p>
          </div>
          <div className="space-y-1">
            <p className="text-[10px] font-bold text-[#706E6B] uppercase tracking-wider">Created By</p>
            <p className="text-[13px] font-medium text-[#2E2E2E]">{task.createdByName || '—'}</p>
          </div>
          <div className="space-y-1">
            <p className="text-[10px] font-bold text-[#706E6B] uppercase tracking-wider">Last Modified By</p>
            <p className="text-[13px] font-medium text-[#2E2E2E]">{task.updatedByName || '—'}</p>
          </div>
          <div className="space-y-1">
            <p className="text-[10px] font-bold text-[#706E6B] uppercase tracking-wider">Modified On</p>
            <p className="text-[13px] font-medium text-[#706E6B]">{task.updatedAt ? formatDate(task.updatedAt) : '—'}</p>
          </div>
        </div>
      </div>

      {/* Tabs Control */}
      <div className="flex items-center border-b border-[#D8DDE6] gap-8">
        {TABS.map(t => (
          <button
            key={t.id}
            onClick={() => setActiveTab(t.id)}
            className={cn(
              "pb-3 text-[13px] font-bold border-b-2 transition-all",
              activeTab === t.id ? "border-[#0176D3] text-[#0176D3]" : "border-transparent text-[#706E6B] hover:text-[#2E2E2E]"
            )}
          >
            {t.label}
          </button>
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
        {/* Main Content Area */}
        <div className="lg:col-span-8 space-y-6">
          {activeTab === 'overview' && (
            <div className="sf-card p-8 bg-white space-y-8 animate-in fade-in">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                <div className="space-y-1.5">
                  <label className="text-[11px] font-semibold text-[#706E6B]">Subject</label>
                  {isEditing ? (
                    <input className="w-full h-10 px-3 bg-[#F3F2F2] border border-[#D8DDE6] rounded text-[13px] focus:border-[#0176D3] focus:ring-1 focus:ring-[#0176D3]" value={formData.title} onChange={e => setFormData({ ...formData, title: e.target.value })} />
                  ) : (
                    <p className="text-[14px] font-medium text-[#2E2E2E] border-b border-dashed border-[#D8DDE6] pb-1">{task.title}</p>
                  )}
                </div>
                <div className="space-y-1.5">
                  <label className="text-[11px] font-semibold text-[#706E6B]">Priority</label>
                  {isEditing ? (
                    <select className="w-full h-10 px-3 bg-[#F3F2F2] border border-[#C9C9C9] rounded text-[13px] font-bold outline-none focus:bg-white focus:border-[#0176D3] transition-all" value={formData.priority} onChange={e => setFormData({ ...formData, priority: e.target.value })}>
                      {PRIORITIES.map(p => <option key={p} value={p}>{p.toUpperCase()}</option>)}
                    </select>
                  ) : (
                    <p className="text-[14px] font-medium text-[#2E2E2E] border-b border-dashed border-[#D8DDE6] pb-1 uppercase">{task.priority || 'medium'}</p>
                  )}
                </div>
                <div className="space-y-1.5">
                  <label className="text-[11px] font-semibold text-[#706E6B]">Type</label>
                  {isEditing ? (
                    <select className="w-full h-10 px-3 bg-[#F3F2F2] border border-[#C9C9C9] rounded text-[13px] font-bold outline-none focus:bg-white focus:border-[#0176D3] transition-all" value={formData.type} onChange={e => setFormData({ ...formData, type: e.target.value })}>
                      {TASK_TYPES.map(t => <option key={t} value={t}>{t.toUpperCase()}</option>)}
                    </select>
                  ) : (
                    <p className="text-[14px] font-medium text-[#2E2E2E] border-b border-dashed border-[#D8DDE6] pb-1 uppercase">{task.type || 'follow_up'}</p>
                  )}
                </div>
                <div className="space-y-1.5">
                  <label className="text-[11px] font-semibold text-[#706E6B]">Due Date</label>
                  {isEditing ? (
                    <input type="datetime-local" className="w-full h-10 px-3 bg-[#F3F2F2] border border-[#D8DDE6] rounded text-[13px] focus:border-[#0176D3] focus:ring-1 focus:ring-[#0176D3]" value={formData.dueDate ? formData.dueDate.slice(0, 16) : ''} onChange={e => setFormData({ ...formData, dueDate: e.target.value })} />
                  ) : (
                    <p className="text-[14px] font-medium text-[#2E2E2E] border-b border-dashed border-[#D8DDE6] pb-1">{task.dueDate ? formatDateTime(task.dueDate) : 'Not set'}</p>
                  )}
                </div>
                <div className="space-y-1.5">
                  <label className="text-[11px] font-semibold text-[#706E6B]">Visibility</label>
                  {isEditing ? (
                    <select className="w-full h-10 px-3 bg-[#F3F2F2] border border-[#C9C9C9] rounded text-[13px] font-bold outline-none focus:bg-white focus:border-[#0176D3] transition-all" value={formData.visibility || 'team'} onChange={e => setFormData({ ...formData, visibility: e.target.value })}>
                      <option value="team">Team — everyone can see this</option>
                      <option value="private">Private — only owner and shared users</option>
                    </select>
                  ) : (
                    <p className="text-[14px] font-medium text-[#2E2E2E] border-b border-dashed border-[#D8DDE6] pb-1">{task.visibility === 'private' ? 'Private' : 'Team'}</p>
                  )}
                </div>
              </div>

              <div className="space-y-2">
                <label className="text-[11px] font-semibold text-[#706E6B]">Notes</label>
                {isEditing ? (
                  <textarea className="w-full min-h-[120px] p-4 bg-[#F3F2F2] border border-[#D8DDE6] rounded text-[13px] text-[#2E2E2E] resize-none focus:border-[#0176D3] focus:ring-1 focus:ring-[#0176D3]" value={formData.description || ''} onChange={e => setFormData({ ...formData, description: e.target.value })} placeholder="Add notes about this task..." />
                ) : (
                  <div className="p-4 bg-[#F4F6F9] rounded border border-[#D8DDE6] text-[13px] text-[#2E2E2E] min-h-[120px] shadow-inner font-medium">
                    {task.description || 'No tactical intel recorded for this directive.'}
                  </div>
                )}
              </div>
            </div>
          )}

          {activeTab === 'files' && (
            <div className="sf-card p-4 animate-in slide-in-from-bottom-4">
              <RelatedFiles module="tasks" moduleId={taskId} recordName={task?.title} />
            </div>
          )}

          {activeTab === 'history' && (
            <AuditHistory resourceType="task" resourceId={taskId} />
          )}
        </div>

        {/* Sidebar */}
        <div className="lg:col-span-4 space-y-6">
          <div className="sf-card p-6 bg-white space-y-6">
            <h3 className="text-[11px] font-bold text-[#706E6B] border-b border-[#D8DDE6] pb-3 mb-4">Task Status</h3>

            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <span className="text-[12px] font-medium text-[#706E6B]">Status</span>
                <span className={cn('sf-badge', isClosed ? 'sf-badge-success' : 'sf-badge-neutral')}>{(task.status || 'pending').toUpperCase()}</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-[12px] font-medium text-[#706E6B]">Reminder</span>
                <span className="text-[12px] font-bold text-[#2E2E2E] flex items-center gap-1.5"><Timer size={12} /> {task.reminderAt ? formatDateTime(task.reminderAt) : 'Not set'}</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-[12px] font-medium text-[#706E6B]">Created</span>
                <span className="text-[12px] font-bold text-[#2E2E2E]">{formatDate(task.createdAt)}</span>
              </div>
            </div>
          </div>

          <div className="sf-card p-6 bg-[#F4F6F9] border border-dashed border-[#D8DDE6] space-y-2">
            <p className="text-[10px] font-bold text-[#706E6B] flex items-center gap-2"><CheckCircle size={14} className="text-[#2E844A]" /> Lifecycle Tracking</p>
            <p className="text-[12px] text-[#2E2E2E] font-medium leading-relaxed italic opacity-80">This directive is tracked in the Revenue Command Hub and will surface in daily operational digests until resolved.</p>
          </div>
        </div>
      </div>

      <ShareModal
        isOpen={showShareModal}
        onClose={() => setShowShareModal(false)}
        resourceType="task"
        resourceId={task._id}
        resourceLabel="Task"
      />
      <TransferOwnershipModal
        isOpen={showTransferModal}
        onClose={() => setShowTransferModal(false)}
        resourceType="task"
        resourceId={task._id}
        currentOwnerId={task.ownerId}
        onTransferred={() => fetchTaskData()}
      />
    </div>
  );
}
